import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { integrations } from '@/data/integrations';
import { ArrowUpRight, Zap } from 'lucide-react';

const categories = [...new Set(integrations.map((i) => i.category))].map((name) => ({
  name,
  items: integrations.filter((i) => i.category === name),
}));

export default function Integrations() {
  return (
    <>
      <SEO
        title="Integrations"
        description="Connect Zuri Hub with WhatsApp, Telegram, Instagram, Slack, Shopify, Stripe, Calendly, WordPress, Notion, Jira, and the tools your team already uses."
        path="/integrations"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-3 py-1 text-sm font-semibold text-brand-300">
                <Zap className="h-4 w-4" /> 100+ integrations
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Connect the tools your team <span className="gradient-text">already loves</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300 lg:mx-0">
                Zuri Hub integrates with your favorite messaging, productivity, payment, and marketing apps so every conversation has context.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
                >
                  Start connecting <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/60 px-7 py-3.5 text-base font-bold text-slate-200 transition hover:bg-slate-800"
                >
                  Book a demo
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-900/60 p-2 backdrop-blur">
              <img
                src="https://media.getzurihub.com/Jayrah%20Assets/header-integrations.webp"
                alt="ZuriHub integrations dashboard"
                className="rounded-2xl w-full h-auto"
                width="800"
                height="600"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {categories.map((category) => (
              <div key={category.name}>
                <h2 className="mb-8 text-2xl font-bold text-slate-900">{category.name}</h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {category.items.map((item) => (
                    <Link
                      key={item.id}
                      to={`/integrations/${item.id}`}
                      className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                    >
                      <item.Icon name={item.iconType === 'brand' ? item.name : undefined} className="h-9 w-9" style={{ color: item.color }} />
                      <span className="text-base font-bold text-slate-900">{item.name}</span>
                      <ArrowUpRight className="ml-auto h-4 w-4 text-slate-400 transition group-hover:text-brand-600" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
